class MainProduct extends HHBaseElement {
  constructor() {
    super()
    this.variantChangeCallbacks = []
    this._isReadyPromise = new Deferred()
    this.productForm = this.querySelector('#product-form-installment')
    this.quickViewFormSubmitButton = this.querySelector(
      '.atc-quick-shop .js-quickview-form-submit'
    )
    this._handleFastBundleVariantChange =
      this._handleFastBundleVariantChange.bind(this)
    this.selectCallback = this.selectCallback.bind(this)
    this.bundleSelectCallback = this.bundleSelectCallback.bind(this)
    this._updatePricing = this._updatePricing.bind(this)
    this._updateFixedATC = this._updateFixedATC.bind(this)
    this._initVariant = null

    // TODO: select the correct selling plan
    this.subscriptionPercent = this.product.selling_plan_groups
      ? this.product.selling_plan_groups[0]?.selling_plans[0]
          .price_adjustments[0].value
      : 0

    // Update the ATC price when a subscription is selected / unselected
    this.subscriptionObserser = new MutationObserver((mutationRecords) => {
      for (const mutation of mutationRecords) {
        if (
          mutation.type === 'attributes' &&
          ['subscribed', 'product', 'frequency'].includes(
            mutation.attributeName
          )
        ) {
          const ogOffer = mutation.target
          const isSubscribed = ogOffer.hasAttribute('subscribed')
          const bwpButton = this.querySelector('#amzn-buy-now')
          const bwpFrame = document.querySelector('#bwpFrame')
          const divider = document.querySelector('.product-details__divided-line')
          if (bwpButton) {
            bwpButton.style.display = isSubscribed ? 'none' : ''
          }
          if (bwpFrame) {
            bwpFrame.style.display = isSubscribed ? 'none' : ''
          }
          if (divider) {
            divider.style.display = isSubscribed ? 'none' : ''
          }

          this.updateAtcPrice()
        }
      }
    })

    if (this.orderGrooveEnabled) {
      this.updateAtcPrice()
      if (this.querySelector('og-offer') && this.withHistoryState) {
        this.subscriptionObserser.observe(this.querySelector('og-offer'), {
          attributes: true,
        })
      }
    }
  }

  get selectedVariant() {
    const selectedVariant = this.querySelector('.selected-variant')

    if (selectedVariant) {
      const matchingVariant = this.product.variants.find(
        (v) => String(v.id) === String(selectedVariant.value)
      )

      return matchingVariant ?? this.product.variants[0]
    }

    return this.product.variants[0]
  }

  get originalPrice() {
    return Shopify.formatMoney(this.selectedVariant.price, window.money_format)
  }

  get subscriptionPrice() {
    const subscriptionSelected = this.querySelector(
      'og-optin-button[subscribed]'
    )

    if (subscriptionSelected && subscriptionSelected.__subscribed) {
      const matchingVariant = this.product.variants.find(
        (v) => String(v.id) === String(subscriptionSelected.__subscribed.id)
      )

      if (matchingVariant) {
        const sellingPlan = matchingVariant.selling_plan_allocations.find(
          (spa) =>
            String(spa.selling_plan_id) ===
            String(subscriptionSelected.__subscribed.frequency)
        )

        if (sellingPlan) {
          return Shopify.formatMoney(sellingPlan.price, window.money_format)
        }
      }
    }

    return this.originalPrice
  }

  get atcPrice() {
    const subscriptionSelected = this.querySelector(
      'og-optin-button[subscribed]'
    )

    return subscriptionSelected ? this.subscriptionPrice : this.originalPrice
  }

  get productId() {
    return this.getAttribute('product-id')
  }

  get productURL() {
    return this.getAttribute('product-url')
  }

  get sectionID() {
    return this.getAttribute('section-id')
  }

  get product() {
    return JSON.parse(
      decodeURIComponent(
        escape(
          window.atob(
            this.querySelector(`#main-product-data-${this.productId}`).innerText
          )
        )
      )
    )
  }

  get currency() {
    return this.getAttribute('currency')
  }

  get variantType() {
    return this.getAttribute('variant-type')?.trim()
  }

  get withHistoryState() {
    return Boolean(this.getAttribute('with-history') === 'true')
  }

  get orderGrooveEnabled() {
    return Boolean(this.getAttribute('order-groove') === 'true')
  }

  get variantStock() {
    return window[`variantStock${this.product.id}`]
  }

  get enableCordial() {
    return this.dataset.enableCordial === 'true'
  }

  get isShowWaitlist() {
    return this.dataset.showWaitlist === 'true'
  }

  isInQuickView(element) {
    return element.closest('quick-shop') !== null
  }

  updateAtcPrice() {
    let priceEffectiveDiv = this.querySelector(
      '.single-product-page .price__regular .price-item--effective'
    )

    const subscriptionSelected = this.querySelector(
      'og-optin-button[subscribed]'
    )

    const oneTimePrice = this.querySelectorAll(
      'og-price[regular]'
    )

    const ogUpsellModal = this.querySelector('og-upsell-modal')

    if (oneTimePrice && priceEffectiveDiv) {
      oneTimePrice.forEach((ogPrice) => {
        ogPrice.shadowRoot.innerHTML = `
          <slot name="prepend"></slot>
          <span>${ priceEffectiveDiv.dataset.eppPrice }</span>
          <slot name="append"></slot>
        `
      })
    }

    // Upsell items are always 10% - don't need to update the main ATC button in this case
    if (ogUpsellModal && ogUpsellModal.hasAttribute('show')) return;

    this.querySelectorAll('.atc-price').forEach((el) => {
      if (priceEffectiveDiv && !subscriptionSelected) {
        el.innerHTML = priceEffectiveDiv.innerHTML
      } else {
        el.textContent = this.atcPrice
      }
    })
  }

  clearSelectedSizes() {
    let allSelectedSizes = [
      ...this.querySelectorAll('.size-select-container.selected'),
    ]
    allSelectedSizes.forEach((item) => item.classList.remove('selected'))
  }

  clearSelectedColors() {
    let allSelectedSwatches = [
      ...this.querySelectorAll('.color-select-container.selected'),
    ]
    allSelectedSwatches.forEach((item) => item.classList.remove('selected'))
  }

  handleSelectedVariantChange(variant) {
    this.variantChangeCallbacks.forEach((cb) => cb?.(variant))
  }

  registerVariantChangeCallback(cb) {
    this.variantChangeCallbacks.push(cb)
  }

  initBundle() {
    if (this._initVariant) {
      this.bundleSelectCallback(this._initVariant)
    }
  }

  bundleSelectCallback(variant) {
    if (!this._initVariant) {
      this._initVariant = variant
      return
    }

    this.handleSelectedVariantChange(variant)
    this._updateBuyWithPrime(variant)
    this._updateDeliveryMessage(variant)
    this._updateVariantInput(variant)
    this._updateViewMoreLink(variant)

    // Update variant dropdowns
    variant.options.forEach((option, index) => {
      const variantDropdown = this.querySelector(
        `variant-dropdown[data-option-target='option${index + 1}']`
      )
      if (variantDropdown?.isReady) {
        variantDropdown.variantName = option
      }
    })

    this._updatePricing(variant)
    this._updateSellFastBadges(variant)
    this._updateFixedATC(variant)
  }

  selectCallback(variant, selector) {
    if (variant) {
      const isQuickView = selector.selectors.some((s) =>
        this.isInQuickView(s.element)
      )

      this.handleSelectedVariantChange(variant)
      if (!isQuickView || !window.location.href.includes('/products/')) {
        this._updateBuyWithPrime(variant)
        this._updateDeliveryMessage(variant)
      }
      if (
        selector.domIdPrefix === 'size-variants' ||
        selector.domIdPrefix === 'size-variants-quick'
      ) {
        // size logic

        // 1.clear selected size and select new size
        this.clearSelectedSizes()
        let newSelectedSize = this.querySelector(
          `.options-wrapper li[data-value="${variant.title}"] .size-select-container`
        )
        newSelectedSize.classList.add('selected')

        let variantDropdowns = this.querySelectorAll('variant-dropdown')
        variantDropdowns.forEach((dropdown) =>
          dropdown.setAttribute('variant', variant.id)
        )
      } else if (
        selector.domIdPrefix === 'general-variants' ||
        selector.domIdPrefix === 'general-variants-quick'
      ) {
        let variantDropdowns = this.querySelectorAll('variant-dropdown')
        variantDropdowns.forEach((dropdown) =>
          dropdown.setAttribute('variant', variant.id)
        )
      } else {
        // update color dropdowns sort logic
        let variantDropdowns = this.querySelectorAll('variant-dropdown')
        variantDropdowns.forEach((dropdown) =>
          dropdown.setAttribute('variant', variant.id)
        )

        // // 3. Update selected color variant
        this.clearSelectedColors()

        let variantMapValue = this.variantMap.filter(
          (mappedVariant) => mappedVariant.optionGenName === variant.title
        )?.[0]?.name

        let newSelectedItem = this.querySelector(
          `.options-wrapper div[data-value="${variantMapValue}"] .color-select-container`
        )

        newSelectedItem?.classList.add('selected')

        // Apply to VTO
        if (
          typeof this.querySelector('virtual-try-on')
            ?._handleSelectedVariantChange === 'function'
        ) {
          this.querySelector('virtual-try-on')._handleSelectedVariantChange()
        }
      }

      this._updateVariantInput(variant)
      this._updateViewMoreLink(variant)

      // 2. Update product-gallery
      let productGalleries = this.querySelectorAll('product-gallery')
      productGalleries.forEach((gallery) =>
        gallery.setAttribute('variant', variant.title)
      )

      // 4. Update pricing
      this._updatePricing(variant)

      // 5. Update Selling Fast badge based on variant inventory - there are currently two instances of the badge
      this._updateSellFastBadges(variant)
    }

    // 6. Update fixed atc
    this._updateFixedATC(variant)
  }

  _updateVariantInput(variant) {
    const variantInput = this.querySelector('.selected-variant')
    variantInput.value = variant.id
  }

  _updateViewMoreLink(variant) {
    let viewMoreLink = this.querySelector('.view-full-details-link a')
    if (viewMoreLink) {
      let newViewMoreLink = new URL(viewMoreLink.href)
      newViewMoreLink.searchParams.set('variant', variant.id)
      viewMoreLink.href = newViewMoreLink?.href
    }
  }

  _updatePricing(variant) {
    let variantPrice = variant.price / 100
    let compareVariantPrice = variant?.compare_at_price / 100

    let priceParent = this.querySelector('.single-product-page .price ')
    let normalPriceContainer = this.querySelector(
      '.single-product-page .price__regular'
    )
    let salePriceContainer = this.querySelector(
      '.single-product-page .price__sale '
    )
    let showSegmentPrice = this.querySelector('.single-product-page .price--segment')
    let addToCartButton = this.querySelectorAll(
      '.single-product-page .atc-container button'
    )
    let addToCartButtonPrices = this.querySelectorAll(
      '.single-product-page .atc-container button .atc-price'
    )
    let addToCartButtonTitle = this.querySelectorAll(
      '.single-product-page .atc-container button span:nth-child(1)'
    )

    const nonAvailableClass = this.isShowWaitlist
      ? atcStatusTitle.notifyMeClass
      : atcStatusTitle.outOfStockClass
    const nonAvailableTitle = this.isShowWaitlist
      ? atcStatusTitle.notifyMe
      : atcStatusTitle.outOfStock

    if (variant.compare_at_price) {
      // reveal compare price
      priceParent.classList.add('price--on-sale')
      normalPriceContainer.classList.add('hidden')

      variantPrice = variantPrice.toLocaleString('en-US', {
        style: 'currency',
        currency: this.currency,
      })
      compareVariantPrice = compareVariantPrice.toLocaleString('en-US', {
        style: 'currency',
        currency: this.currency,
      })

      if (salePriceContainer) {
        salePriceContainer.classList.remove('hidden')
        // set new variant price
        let compareVariantDiv = salePriceContainer.querySelector(
          '.price-item--regular'
        )

        let priceDiv = salePriceContainer.querySelector('.price-item--sale')

        if ( !showSegmentPrice ){
          compareVariantDiv.innerHTML = compareVariantPrice
          priceDiv.innerHTML = variantPrice
        }
      }

      if (variant.available) {
        addToCartButton.forEach((button) => {
          button.classList.remove(...nonAvailableClass)
          button.classList.add(atcStatusTitle.addToBagClass)
          button.removeAttribute('data-target')
        })
        addToCartButtonPrices.forEach(
          (price) => (price.innerHTML = variantPrice)
        )
        addToCartButtonTitle.forEach(
          (title) => (title.innerHTML = atcStatusTitle.addToBag)
        )
        addToCartButton.forEach((button) => (button.disabled = false))
      } else {
        addToCartButton.forEach((button) => {
          button.classList.remove(atcStatusTitle.addToBagClass)
          button.classList.add(...nonAvailableClass)
          button.setAttribute('data-target', atcStatusTitle.targetID)
        })
        addToCartButtonTitle.forEach(
          (title) => (title.innerHTML = nonAvailableTitle)
        )
        if (!this.isShowWaitlist) {
          addToCartButton.forEach((button) => (button.disabled = true))
        }
      }
    } else {
      // hide compare price
      priceParent.classList.remove('price--on-sale')
      normalPriceContainer.classList.remove('hidden')
      salePriceContainer?.classList.add('hidden')

      // set new variant price
      let priceDiv = normalPriceContainer.querySelector('.price-item--regular')
      let priceEffectiveDiv = normalPriceContainer.querySelector(
        '.price-item--effective'
      )

      variantPrice = variantPrice.toLocaleString('en-US', {
        style: 'currency',
        currency: this.currency,
      })

      if (priceDiv) {
        priceDiv.innerHTML = variantPrice
      } else if (priceEffectiveDiv) {

        this._updateEffectivePrice(variant)
        priceDiv = priceEffectiveDiv
        variantPrice = priceEffectiveDiv.innerHTML
        priceDiv.innerHTML = variantPrice
      }

      if (variant.available) {
        addToCartButton.forEach((button) => {
          button.classList.remove(...nonAvailableClass)
          button.classList.add(atcStatusTitle.addToBagClass)
          button.removeAttribute('data-target')
        })

        addToCartButtonPrices.forEach(
          (price) => (price.innerHTML = variantPrice)
        )
        addToCartButtonTitle.forEach(
          (title) => (title.innerHTML = atcStatusTitle.addToBag)
        )
        addToCartButton.forEach((button) => (button.disabled = false))
      } else {
        addToCartButton.forEach((button) => {
          button.classList.remove(atcStatusTitle.addToBagClass)
          button.classList.add(...nonAvailableClass)
          button.setAttribute('data-target', atcStatusTitle.targetID)
        })
        addToCartButtonTitle.forEach(
          (title) => (title.innerHTML = nonAvailableTitle)
        )
        if (!this.isShowWaitlist) {
          addToCartButton.forEach((button) => (button.disabled = true))
        }
      }
    }
  }

  _updateEffectivePrice(variant) {
    fetch(
      `${this.productURL}?variant=${variant.id}&section_id=${this.sectionID}`
    )
      .then((response) => response.text())
      .then((responseText) => {

        const resultsMarkup = new DOMParser()
            .parseFromString(responseText, 'text/html')
        const source = resultsMarkup.querySelector(`.single-product-page .price__regular .price-item--effective`)
        const destination = this.querySelector(`.single-product-page .price__regular .price-item--effective`)

        if ( source && destination) destination.innerHTML = source.innerHTML
      })
  }

  _updateSellFastBadges(variant) {
    let sellFastBadges = [
      ...this.querySelectorAll('.single-product-page .selling-fast-container'),
    ]

    if (sellFastBadges.length > 0) {
      let variantInventory = parseFloat(this.variantStock?.[variant.id]) || 0
      let sellFastThreshold = parseFloat(
        this.querySelector('.single-product-page .selling-fast-container')
          .dataset.threshold
      )

      if (variantInventory <= sellFastThreshold) {
        sellFastBadges.forEach((badge) => badge.classList.remove('hidden'))
      } else {
        sellFastBadges.forEach((badge) => badge.classList.add('hidden'))
      }
    }
  }

  _updateFixedATC(variant) {
    let fixedAtc = this.querySelector('.fixed-add-to-cart__container')

    if (fixedAtc && window.innerWidth > 992) {
      let fixedVariantTitle = this.querySelector(
        '.fixed-add-to-cart__container .current-variant__title'
      )

      let doesProductHaveColor =
        [...this.querySelectorAll('.swatch-element.color')].length > 0

      if (variant) {
        fixedVariantTitle.innerText = variant.title

        if (doesProductHaveColor) {
          let currentVariantShade = this.querySelector(
            '.fixed-add-to-cart__container .swatch-color-span'
          )
          let newVariantColorEl = this.querySelector(
            'span[data-tooltip="' + variant.title + '"] button'
          )

          if (newVariantColorEl) {
            currentVariantShade.style.backgroundColor =
              newVariantColorEl.style.backgroundColor
          }
        }
      }
    }
  }

  _handleFastBundleVariantChange(event) {
    try {
      const selectedItems = event.detail.selectedItems.filter((id) =>
        Boolean(id)
      )
      if (selectedItems.length > 0) {
        for (const item of selectedItems) {
          const variantId = item.id
          const matchingSelector = this.querySelector(
            `[data-variant-id="${variantId}"]`
          )
          if (matchingSelector) {
            matchingSelector.dispatchEvent(new Event('click'))
            break
          }
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  _updateBuyWithPrime(variant) {
    try {
      const target = this.querySelector('#amzn-buy-now')
      if (target) {
        target.dataset.sku = variant.sku
      }

      // Seperate the reviews and ratings from the buy now button
      const reviewsAndRatings = this.closest('main')?.querySelectorAll(
        '#amzn-roa-rating, #amzn-roa-reviews'
      )

      if (reviewsAndRatings) {
        for (const element of reviewsAndRatings) {
          element.dataset.sku = variant.sku
        }
      }

      if (window.bwp) {
        window.bwp.updateWidget(variant.sku)
      }
    } catch (error) {}
  }

  _updateDeliveryMessage(variant) {
    this.querySelectorAll('[data-adapters*="shopify-variant-sku-adapter"]').forEach(
      (element) => {
        element.setAttribute('product-sku', variant.sku);
      }
    )
  }

  initBundleOptionSelector() {
    this.optionsSelector = new Shopify.OptionSelectors(
      `product-${this.productId}-bundle-variant-select`,
      {
        product: this.product,
        onVariantSelected: this.bundleSelectCallback,
        enableHistoryState: this.withHistoryState,
      }
    )
  }

  initOptionSelector() {
    if (this.variantType === 'none' || !this.variantType) {
      return
    }

    this.optionsSelector = new Shopify.OptionSelectors(this.variantType, {
      product: this.product,
      onVariantSelected: this.selectCallback,
      enableHistoryState: this.withHistoryState,
    })

    let selector = this.querySelector('.single-option-selector')
    let options = [...selector.querySelectorAll('option')]

    let liquidVariants = this.querySelector('.hidden-variants')
    let liquidOptions = [...liquidVariants.querySelectorAll('option')]

    let rawVariants = this.product.variants

    let uniqueSelector = options.map((option, index) => {
      let newObject = {
        value: rawVariants[index].id,
        name: liquidOptions[index].innerText,
        optionGenName: option.value,
      }

      return newObject
    })

    this.variantMap = uniqueSelector
  }

  _handleQuickviewFormSubmitClick(e) {
    // Ensures that the quickview product form is submitted rather than the PDP product form
    e.preventDefault()
    this.productForm.requestSubmit()
  }

  disconnectedCallback() {
    this.subscriptionObserser.disconnect()
    document.removeEventListener(
      'fb:single-bap-variant-changed',
      this._handleFastBundleVariantChange
    )
    this.quickViewFormSubmitButton?.removeEventListener(
      'click',
      this._handleQuickviewFormSubmitClick.bind(this)
    )
  }

  _togglePopup(){
    const urlParams = new URLSearchParams(window.location.search)
    const virtualTryOnParam = urlParams.get('virtual-try-on')

    if ( virtualTryOnParam ){
      const virtualTryOn = this.querySelector('button[data-target=shade-finder]')
      if ( virtualTryOn ){
        virtualTryOn.click()
      }
    }
  }

  connectedCallback() {
    if (this.product.type.toLowerCase() === 'bundle') {
      this.initBundleOptionSelector()
    } else {
      this.initOptionSelector()
    }

    this.triggerEvent('main-product::ready')
    document.addEventListener(
      'fb:single-bap-variant-changed',
      this._handleFastBundleVariantChange
    )
    this.quickViewFormSubmitButton?.addEventListener(
      'click',
      this._handleQuickviewFormSubmitClick.bind(this)
    )

    this._togglePopup()
    this._isReadyPromise.resolve()
  }

  get isReadyPromise() {
    return this._isReadyPromise.promise
  }
}

window.customElements.define('main-product', MainProduct)
